import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  KudosTypeResponse,
  KudosTypeRequest,
} from "@/types/generated/cms.generated";

const KUDOS_TYPES_KEY = ["kudos-types"] as const;

// Fetch the full kudos type catalog
export function useKudosTypes() {
  return useQuery<KudosTypeResponse[]>({
    queryKey: KUDOS_TYPES_KEY,
    queryFn: () => apiRequest("GET", "/kudos-types"),
  });
}

// Create a kudos type
export function useCreateKudosType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: KudosTypeRequest) =>
      apiRequest<KudosTypeResponse>("POST", "/kudos-types", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KUDOS_TYPES_KEY });
      toast({
        title: "Kudos type created",
        description: "The kudos type has been added to the catalog.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to create kudos type. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Update a kudos type (also used for activate/deactivate and reorder)
export function useUpdateKudosType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: KudosTypeRequest }) =>
      apiRequest<KudosTypeResponse>("PUT", `/kudos-types/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KUDOS_TYPES_KEY });
      toast({
        title: "Kudos type updated",
        description: "The kudos type has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to update kudos type. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Delete a kudos type
export function useDeleteKudosType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/kudos-types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KUDOS_TYPES_KEY });
      toast({
        title: "Kudos type deleted",
        description: "The kudos type has been removed from the catalog.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to delete kudos type. Please try again.",
        variant: "destructive",
      });
    },
  });
}
